import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Mail, KeyRound, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const STEPS = { EMAIL: 0, CODE: 1, NEW_PASSWORD: 2, DONE: 3 };

export default function PasswordResetPage() {
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/password-reset/request', { email });
      toast.success('Code wurde gesendet, falls ein Konto existiert.');
      setStep(STEPS.CODE);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Senden');
    }
    setLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/password-reset/verify', { email, code });
      setStep(STEPS.NEW_PASSWORD);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ungültiger Code');
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Mindestens 6 Zeichen');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/password-reset/confirm', { email, code, new_password: newPassword });
      toast.success('Passwort erfolgreich geändert!');
      setStep(STEPS.DONE);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Zurücksetzen');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6" data-testid="password-reset-page">
      <Card className="w-full max-w-md" data-testid="reset-card">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <img src="/logo.png" alt="LaizyLeads" className="h-10 w-auto" />
          </div>
          <CardTitle className="text-xl">
            {step === STEPS.EMAIL && 'Passwort zurücksetzen'}
            {step === STEPS.CODE && 'Code eingeben'}
            {step === STEPS.NEW_PASSWORD && 'Neues Passwort'}
            {step === STEPS.DONE && 'Erledigt!'}
          </CardTitle>
          <CardDescription>
            {step === STEPS.EMAIL && 'Geben Sie Ihre E-Mail-Adresse ein. Sie erhalten einen 6-stelligen Code.'}
            {step === STEPS.CODE && 'Prüfen Sie Ihr Postfach und geben Sie den Code ein.'}
            {step === STEPS.NEW_PASSWORD && 'Legen Sie ein neues Passwort fest.'}
            {step === STEPS.DONE && 'Ihr Passwort wurde erfolgreich geändert.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === STEPS.EMAIL && (
            <form onSubmit={handleRequestCode} className="space-y-4">
              <div>
                <Label htmlFor="email">E-Mail-Adresse</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="email" type="email" placeholder="name@firma.de" value={email}
                    onChange={(e) => setEmail(e.target.value)} required className="pl-10" data-testid="reset-email-input" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white" disabled={loading} data-testid="reset-send-code-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Code senden'}
              </Button>
            </form>
          )}

          {step === STEPS.CODE && (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div>
                <Label htmlFor="code">6-stelliger Code</Label>
                <div className="relative mt-1.5">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="code" type="text" placeholder="123456" maxLength={6} value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required
                    className="pl-10 text-center text-xl tracking-widest font-mono" data-testid="reset-code-input" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white" disabled={loading} data-testid="reset-verify-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Code prüfen'}
              </Button>
              <button type="button" onClick={() => { setStep(STEPS.EMAIL); setCode(''); }} className="w-full text-sm text-muted-foreground hover:text-foreground text-center">
                Neuen Code anfordern
              </button>
            </form>
          )}

          {step === STEPS.NEW_PASSWORD && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="newPassword">Neues Passwort</Label>
                <Input id="newPassword" type="password" placeholder="Mindestens 6 Zeichen" value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)} required className="mt-1.5" data-testid="reset-new-password-input" />
              </div>
              <div>
                <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                <Input id="confirmPassword" type="password" placeholder="Passwort wiederholen" value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)} required className="mt-1.5" data-testid="reset-confirm-password-input" />
              </div>
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white" disabled={loading} data-testid="reset-confirm-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Passwort ändern'}
              </Button>
            </form>
          )}

          {step === STEPS.DONE && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <Link to="/login">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="reset-to-login-btn">
                  Zum Login
                </Button>
              </Link>
            </div>
          )}

          {step !== STEPS.DONE && (
            <div className="text-center mt-4">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="reset-back-to-login">
                <ArrowLeft className="w-3.5 h-3.5" /> Zurück zum Login
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
